import { Placement } from '@/webapi/use-experience-api';
import { QBItemSelection } from '@/components/query-builder/models';
import { StorePage } from '@/features/editor/context/use-device-navigation';
import { rmTrailingSlash, urlNoPreviewThemeId } from '@/utils/url';
import {
  getPathname,
  getUrlWithoutScheme,
  hasScheme,
  isNotCheckoutPage,
  isSameHost,
  PAGE_PATH_CAPTION,
  PAGE_TYPE_CAPTION,
  PAGE_URL_CAPTION,
  safeDecode,
  toFullUrl,
} from '@/features/editor/widgets/shared/modals/placement-url-utils';

type RuleValue = QBItemSelection['values'][number];

const URL_RULE_CAPTIONS = [
  PAGE_URL_CAPTION,
  PAGE_PATH_CAPTION,
  PAGE_TYPE_CAPTION,
];
const NEGATIVE_OPS = [`isnot`, `notincludes`];
const EXACT_MATCH_OPS = [`is`, `isnot`];

const safeRegexTest = (pattern: string, text: string) => {
  try {
    return new RegExp(pattern).test(text);
  } catch {
    return false;
  }
};

const isNegativeOp = (op: string) => NEGATIVE_OPS.includes(op);

const doesTextMatch = (text: string, { op, value }: RuleValue) => {
  switch (op) {
    case `is`:
    case `isnot`:
      return text === value;
    case `includes`:
    case `notincludes`:
      return text.includes(value);
    case `regex`:
      return safeRegexTest(value, text);
    default:
      return false;
  }
};

const doesFullUrlMatchValue = (url: string, ruleValue: RuleValue) => {
  const urlWithoutScheme = getUrlWithoutScheme(url);
  if (ruleValue.op === `regex`) {
    return (
      safeRegexTest(ruleValue.value, toFullUrl(urlWithoutScheme)) ||
      safeRegexTest(ruleValue.value, urlWithoutScheme)
    );
  }
  return doesTextMatch(urlWithoutScheme, {
    ...ruleValue,
    value: getUrlWithoutScheme(ruleValue.value),
  });
};

const doesPathMatchValue = (url: string, ruleValue: RuleValue) => {
  const pathname = getPathname(url);
  const value = safeDecode(ruleValue.value);
  if (EXACT_MATCH_OPS.includes(ruleValue.op)) {
    return doesTextMatch(rmTrailingSlash(pathname), {
      ...ruleValue,
      value: rmTrailingSlash(value),
    });
  }
  return doesTextMatch(pathname, { ...ruleValue, value });
};

const doesUrlMatchValue = (
  url: string,
  caption: string,
  ruleValue: RuleValue,
) => {
  if (caption === PAGE_TYPE_CAPTION) {
    return safeRegexTest(`^${ruleValue.value}/?$`, getPathname(url));
  }
  if (caption === PAGE_URL_CAPTION) {
    return doesFullUrlMatchValue(url, ruleValue);
  }
  return doesPathMatchValue(url, ruleValue);
};

const doesUrlPassValue = (
  url: string,
  caption: string,
  ruleValue: RuleValue,
) => {
  const matches = doesUrlMatchValue(url, caption, ruleValue);
  return isNegativeOp(ruleValue.op) ? !matches : matches;
};

const getPositiveValues = (row: QBItemSelection) =>
  row.values.filter((v) => !isNegativeOp(v.op));

const getNegativeValues = (row: QBItemSelection) =>
  row.values.filter((v) => isNegativeOp(v.op));

const doesUrlPassPositiveValues = (url: string, row: QBItemSelection) => {
  const positives = getPositiveValues(row);
  return (
    positives.length === 0 ||
    positives.some((v) => doesUrlPassValue(url, row.qbProps.caption, v))
  );
};

const doesUrlPassNegativeValues = (url: string, row: QBItemSelection) =>
  getNegativeValues(row).every((v) =>
    doesUrlPassValue(url, row.qbProps.caption, v),
  );

const doesUrlPassAllPositiveValues = (url: string, rows: QBItemSelection[]) =>
  rows.every((row) => doesUrlPassPositiveValues(url, row));

const doesUrlPassAllRows = (url: string, rows: QBItemSelection[]) =>
  rows.every(
    (row) =>
      doesUrlPassPositiveValues(url, row) &&
      doesUrlPassNegativeValues(url, row),
  );

const hasPositiveValues = (rows: QBItemSelection[]) =>
  rows.some((row) => getPositiveValues(row).length > 0);

const getUrlRuleRows = (selected: Placement) =>
  (selected?.other || []).filter((row) =>
    URL_RULE_CAPTIONS.includes(row?.qbProps?.caption),
  );

const getTypedFullUrls = (rows: QBItemSelection[], domain: string) =>
  rows
    .filter((row) => row.qbProps.caption === PAGE_URL_CAPTION)
    .flatMap((row) => row.values)
    .filter((v) => v.op === `includes` && hasScheme(v.value))
    .map((v) => v.value)
    .filter((url) => isSameHost(url, domain));

const getPreviewableStoreUrls = (storePages: StorePage[]) =>
  (storePages || []).filter(isNotCheckoutPage).map((page) => page.url);

const isSameUrl = (first: string, second: string) =>
  getUrlWithoutScheme(first) === getUrlWithoutScheme(second);

const getStoreUrlsEqualToTypedUrls = (
  storeUrls: string[],
  typedUrls: string[],
) =>
  storeUrls.filter((storeUrl) =>
    typedUrls.some((typedUrl) => isSameUrl(storeUrl, typedUrl)),
  );

export const findPreviewUrlForUrlRules = (
  selected: Placement,
  storePages: StorePage[],
  previewUrl: string,
  domain: string,
) => {
  const rows = getUrlRuleRows(selected);
  const currentUrl = urlNoPreviewThemeId(previewUrl || ``);
  if (
    !hasPositiveValues(rows) ||
    doesUrlPassAllPositiveValues(currentUrl, rows)
  ) {
    return undefined;
  }
  const storeUrls = getPreviewableStoreUrls(storePages);
  const typedUrls = getTypedFullUrls(rows, domain);
  const candidates = [
    ...getStoreUrlsEqualToTypedUrls(storeUrls, typedUrls),
    ...storeUrls,
    ...typedUrls,
  ];
  return candidates.find((url) => doesUrlPassAllRows(url, rows));
};
